import React, { useEffect, useMemo, useState } from 'react';
import { diffWords } from 'diff';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { GitCompare, ArrowLeftRight, Loader2, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { versionsApi } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Side-by-side / unified diff viewer between two chapter versions (iter-13).
 *
 *  The drawer supplies a "left" version (the one the user clicked Compare on)
 *  and the list of all candidates for the "right" pane. We default the right
 *  pane to the immediate previous version when available so the first thing
 *  the user sees is a useful before/after.
 *
 *  Two views:
 *   - Unified word diff (default — easier to read for prose).
 *   - Split side-by-side (full content of each version in two columns).
 */
export function DiffViewerDialog({
  open,
  onClose,
  chapter,
  baseline,       // version to compare AGAINST (older, left side)
  candidate,     // version being viewed (newer, right side)
  versions,      // all versions for the chapter (newest-first)
  onChangeBaseline,
  onRestore,
  canMutate = true,
}) {
  const [view, setView] = useState('unified'); // 'unified' | 'split'
  const [busy, setBusy] = useState(false);

  // Reset to unified whenever we re-open with a new candidate.
  useEffect(() => { if (open) setView('unified'); }, [open, candidate?.id]);

  // Word-level diff: when no baseline, fall back to "everything is added".
  const parts = useMemo(() => {
    if (!candidate) return [];
    const a = baseline?.content || '';
    const b = candidate?.content || '';
    return diffWords(a, b, { ignoreCase: false });
  }, [baseline?.content, candidate]);

  const { added, removed } = useMemo(() => {
    let add = 0, rem = 0;
    for (const p of parts) {
      const wc = p.value ? p.value.trim().split(/\s+/).filter(Boolean).length : 0;
      if (p.added) add += wc;
      else if (p.removed) rem += wc;
    }
    return { added: add, removed: rem };
  }, [parts]);

  const handleRestoreBaseline = async () => {
    if (!canMutate || !baseline) return;
    if (!window.confirm(`Restore v${baseline.version_number} as the current draft?\nYour current content will be snapshotted first so this is reversible.`)) return;
    setBusy(true);
    try {
      await versionsApi.restore(chapter.id, baseline.id);
      toast.success(`Restored v${baseline.version_number}.`);
      onRestore?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed.');
    } finally { setBusy(false); }
  };

  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-5xl max-h-[88vh] flex flex-col gap-0 p-0"
        data-testid="version-diff-dialog"
      >
        <DialogHeader className="border-b border-border bg-panel-header/40 px-4 py-3">
          <DialogTitle className="text-[14px] font-semibold flex items-center gap-2">
            <GitCompare className="h-4 w-4 text-primary" />
            Comparing v{baseline?.version_number ?? '—'} → v{candidate.version_number}
          </DialogTitle>
          <DialogDescription className="text-[13.5px] text-muted-foreground">
            {chapter?.title || `Chapter ${chapter?.number}`} · word-level diff.
            {' '}
            <span className="text-success">+{added} added</span>{' '}
            ·{' '}
            <span className="text-destructive">−{removed} removed</span>
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar — baseline selector + view toggle */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-background px-4 py-2 text-[13.5px]">
          <label className="text-muted-foreground" htmlFor="diff-baseline-select">Compare to:</label>
          <Select
            value={baseline?.id || '__empty__'}
            onValueChange={(val) => {
              if (val === '__empty__') {
                onChangeBaseline?.(null);
              } else {
                const v = versions.find((x) => x.id === val) || null;
                onChangeBaseline?.(v);
              }
            }}
          >
            <SelectTrigger
              id="diff-baseline-select"
              data-testid="diff-baseline-select"
              className="h-7 w-[280px] text-[13.5px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__empty__">(empty draft)</SelectItem>
              {versions
                .filter((v) => v.id !== candidate.id)
                .map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    v{v.version_number} — {(v.title || `Chapter ${chapter?.number}`).slice(0, 32)} ({v.words} words)
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <div className="mx-2 h-4 w-px bg-border" />

          <div className="inline-flex rounded-sm border border-border" role="tablist">
            <button
              role="tab"
              data-testid="diff-view-unified"
              onClick={() => setView('unified')}
              className={cn(
                'px-2.5 py-0.5 text-[13.5px] rounded-l-sm',
                view === 'unified' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              )}
            >
              Unified
            </button>
            <button
              role="tab"
              data-testid="diff-view-split"
              onClick={() => setView('split')}
              className={cn(
                'px-2.5 py-0.5 text-[13.5px] border-l border-border rounded-r-sm',
                view === 'split' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              )}
            >
              <ArrowLeftRight className="inline h-3 w-3 mr-1" /> Split
            </button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {baseline && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRestoreBaseline}
                disabled={!canMutate || busy}
                className="h-7 gap-1.5 text-[13.5px]"
                data-testid="diff-restore-baseline"
                title={`Replace current draft with v${baseline.version_number}`}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Restore v{baseline.version_number}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onClose?.()}
              className="h-7 gap-1.5 text-[13.5px]"
              data-testid="diff-close"
            >
              <X className="h-3 w-3" /> Close
            </Button>
          </div>
        </div>

        {/* Body — diff content */}
        <div className="flex-1 overflow-auto scroll-thin bg-background">
          {view === 'unified' ? (
            <UnifiedDiff parts={parts} candidate={candidate} baseline={baseline} />
          ) : (
            <SplitDiff parts={parts} candidate={candidate} baseline={baseline} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnifiedDiff({ parts, candidate, baseline }) {
  if (!parts.length || (!baseline && !candidate?.content)) {
    return <div className="p-8 text-center text-[13.5px] text-muted-foreground">Both versions are empty.</div>;
  }
  return (
    <div
      data-testid="diff-unified-body"
      className="p-6 leading-relaxed text-[13.5px] font-serif whitespace-pre-wrap break-words"
    >
      {parts.map((p, idx) => {
        // iter-50 — diff parts have no stable id. Tagging the key by lane
        // (added / removed / equal) plus index lets React reliably tell
        // segments apart when the diff is recomputed on edit.
        const k = `u-${idx}-${p.added ? 'a' : p.removed ? 'r' : 'e'}`;
        if (p.added) {
          return (
            <span
              key={k}
              className="bg-success/15 text-success-foreground underline decoration-success/60 decoration-2 underline-offset-2 rounded-sm"
            >
              {p.value}
            </span>
          );
        }
        if (p.removed) {
          return (
            <span
              key={k}
              className="bg-destructive/15 text-destructive line-through decoration-destructive/60 rounded-sm"
            >
              {p.value}
            </span>
          );
        }
        return <span key={k}>{p.value}</span>;
      })}
    </div>
  );
}

function SplitDiff({ parts, baseline, candidate }) {
  // Build the left & right strings, marking removed-only on the left, added-only on the right.
  return (
    <div data-testid="diff-split-body" className="grid grid-cols-2 divide-x divide-border">
      <article className="p-6 leading-relaxed text-[13.5px] font-serif whitespace-pre-wrap break-words">
        <div className="mb-2 text-[13px] uppercase tracking-wider text-muted-foreground">
          v{baseline?.version_number ?? '—'} (older)
        </div>
        {parts.map((p, idx) => {
          const k = `l-${idx}-${p.removed ? 'r' : 'e'}`;
          if (p.added) return null;
          if (p.removed) {
            return (
              <span
                key={k}
                className="bg-destructive/15 text-destructive line-through decoration-destructive/60 rounded-sm"
              >
                {p.value}
              </span>
            );
          }
          return <span key={k}>{p.value}</span>;
        })}
      </article>
      <article className="p-6 leading-relaxed text-[13.5px] font-serif whitespace-pre-wrap break-words">
        <div className="mb-2 text-[13px] uppercase tracking-wider text-muted-foreground">
          v{candidate.version_number} (newer)
        </div>
        {parts.map((p, idx) => {
          const k = `r-${idx}-${p.added ? 'a' : 'e'}`;
          if (p.removed) return null;
          if (p.added) {
            return (
              <span
                key={k}
                className="bg-success/15 text-success-foreground underline decoration-success/60 decoration-2 underline-offset-2 rounded-sm"
              >
                {p.value}
              </span>
            );
          }
          return <span key={k}>{p.value}</span>;
        })}
      </article>
    </div>
  );
}
